// Smart Download modal (admin Students tab).
//
// Replaces the four separate export buttons (Export .xlsx / DocuSign Bulk /
// Quarterly Report / PTIB). The admin picks a preset, tweaks which filters
// to apply (defaults mirror the active filters), and sees a LIVE row count
// before hitting Export. The parent passes down the current filter state
// and the callbacks that actually trigger each export.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { API } from "@/config/api";
import { DownloadSimple, CircleNotch, FileXls, FileText, Buildings, FileArrowDown } from "@phosphor-icons/react";

const PRESETS = [
  { id: "standard",   label: "Student Report (.xlsx)",   desc: "Full student roster with contact, program, intake and stage",   icon: FileXls },
  { id: "quarterly",  label: "Quarterly Report Format",   desc: "Quarterly submission template with the standard column set",   icon: FileText },
  { id: "docusign",   label: "DocuSign Bulk Upload",      desc: "29-column template with program dates and tuition split",      icon: FileArrowDown },
  { id: "ptib",       label: "PTIB Student Report",        desc: "PTIB-formatted roster scoped to the selected recruiter",       icon: Buildings },
];

export default function StudentsDownloadModal({
  open, onOpenChange,
  filters,
  onExportStandard,
  onExportQuarterly,
  onExportDocusign,
  onExportPTIB,
}) {
  const [preset, setPreset] = useState("standard");
  // Local copy of the filters so toggles here don't bleed into the main page.
  const [local, setLocal] = useState(filters);
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { if (open) setLocal(filters); }, [open, filters]);

  const paramsFor = (f) => {
    const p = new URLSearchParams();
    if (f.recruiter_id && f.recruiter_id !== "all") p.set("recruiter_id", f.recruiter_id);
    if ((f.status || []).length) p.set("status", f.status.join(","));
    if (f.college_id && f.college_id !== "all") p.set("college_id", f.college_id);
    if (f.intake && f.intake !== "all") p.set("intake", f.intake);
    if (f.search) p.set("search", f.search);
    if (f.start_date) p.set("start_date", f.start_date);
    if (f.end_date) p.set("end_date", f.end_date);
    return p;
  };

  // Fetch live count whenever the local filters change.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCount(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/students/count?${paramsFor(local)}`);
        if (!cancelled) setCount(data.count);
      } catch { if (!cancelled) setCount(null); }
      finally { if (!cancelled) setLoadingCount(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, local]);

  // Filter toggle rows — ticking "off" removes the filter from this export only.
  const activeChips = useMemo(() => {
    const chips = [];
    if (local.search) chips.push({ key: "search", label: `Search: ${local.search}`, clear: () => setLocal({ ...local, search: "" }) });
    if (local.recruiter_id && local.recruiter_id !== "all") chips.push({ key: "rec", label: `Recruiter · ${local._recruiter_label || "selected"}`, clear: () => setLocal({ ...local, recruiter_id: "all" }) });
    if ((local.status || []).length) chips.push({ key: "st", label: `Status · ${(local.status).join(", ")}`, clear: () => setLocal({ ...local, status: [] }) });
    if (local.college_id && local.college_id !== "all") chips.push({ key: "col", label: `College · ${local._college_label || "selected"}`, clear: () => setLocal({ ...local, college_id: "all" }) });
    if (local.intake && local.intake !== "all") chips.push({ key: "int", label: `Intake · ${local.intake}`, clear: () => setLocal({ ...local, intake: "all" }) });
    if (local.start_date || local.end_date) chips.push({ key: "dt", label: `Date · ${local.start_date || "any"} → ${local.end_date || "any"}`, clear: () => setLocal({ ...local, start_date: "", end_date: "" }) });
    return chips;
  }, [local]);

  const handleExport = async () => {
    setRunning(true);
    try {
      if (preset === "standard" && onExportStandard) await onExportStandard(local);
      else if (preset === "quarterly" && onExportQuarterly) await onExportQuarterly(local);
      else if (preset === "docusign" && onExportDocusign) await onExportDocusign(local);
      else if (preset === "ptib" && onExportPTIB) await onExportPTIB(local);
      onOpenChange(false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="students-download-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DownloadSimple size={18} /> Download students</DialogTitle>
          <DialogDescription>
            Pick a format, double-check the filters, and export. Row count updates live as you tweak.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Preset picker */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PRESETS.map((p) => {
              const Icon = p.icon;
              const selected = preset === p.id;
              return (
                <button key={p.id} type="button"
                  onClick={() => setPreset(p.id)}
                  className={`text-left rounded-lg border p-3 transition-colors ${selected ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                  data-testid={`dl-preset-${p.id}`}>
                  <div className="flex items-center gap-2">
                    <Icon size={16} className={selected ? "text-indigo-600" : "text-gray-500"} />
                    <div className="text-sm font-medium text-gray-900">{p.label}</div>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1 leading-snug">{p.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Filter mirror */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Filters applied to this export</div>
                <div className="text-[11px] text-gray-500 mt-0.5">Remove any you don&apos;t want for this one export. Your dashboard filters stay as-is.</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Rows</div>
                <div className="text-xl font-bold tabular-nums text-indigo-700" data-testid="dl-row-count">
                  {loadingCount ? "…" : (count ?? "—")}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {activeChips.length === 0 && <span className="text-[11px] text-gray-500 italic">No filters applied — the export will include every student.</span>}
              {activeChips.map((c) => (
                <button key={c.key} type="button"
                  onClick={c.clear}
                  className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 hover:border-rose-300 hover:text-rose-700"
                  title="Remove this filter from the export">
                  {c.label}
                  <span className="opacity-50 group-hover:opacity-100">×</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="dl-cancel">Cancel</Button>
          <Button onClick={handleExport} disabled={running || loadingCount || count === 0} data-testid="dl-export">
            {running ? (<><CircleNotch size={13} className="animate-spin mr-1.5" /> Exporting…</>) : (<><DownloadSimple size={13} className="mr-1.5" /> Export {count != null ? `${count} row${count === 1 ? "" : "s"}` : ""}</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
